import { Box, Button, Flex, Stack, Text } from '@chakra-ui/react';
import React, { PropsWithChildren, useCallback } from 'react';
import ConditionalFragment from '../../../components/common/ConditionalFragment';
import classNames from 'classnames';

interface CustomRadioProps<Value> {
  options: Array<Option<Value | null>>;
  defaultIdx?: null | number;
  onChange: (value: Value) => void;
  activeIdx: Value | null;
  fixColumnStyle?: boolean;
  disableDesktopExtraStyle?: boolean;
}

interface CustomRadioComponent extends FCWithoutComponent {
  <Value>(props: PropsWithChildren<CustomRadioProps<Value>>): ReturnType<React.FC>;
}

const CustomRadio: CustomRadioComponent = ({
  options,
  fixColumnStyle,
  disableDesktopExtraStyle,
  onChange,
  activeIdx,
}) => {
  const onButtonPress = useCallback(
    (item: any) => {
      onChange(item);
    },
    [activeIdx, onChange]
  );

  return (
    <Box
      w={'full'}
      display={'flex'}
      justifyContent={'start'}
      flexDirection={{ md: fixColumnStyle ? undefined : 'row', base: 'column' }}
      flexWrap={{ md: 'wrap' }}
    >
      {options.map(({ label, value, active = true }) => {
        if (!active) return <React.Fragment key={label} />;
        return (
          <RadioItem
            disableDesktopExtraStyle={disableDesktopExtraStyle}
            fixColumnStyle={fixColumnStyle}
            onPress={() => onButtonPress(value)}
            key={label}
            isActive={activeIdx === value}
            label={label}
          />
        );
      })}
    </Box>
  );
};
const RadioItem: React.FC<{
  isActive: boolean;
  label: string;
  onPress: () => void;
  fixColumnStyle?: boolean;
  disableDesktopExtraStyle?: boolean;
}> = ({ isActive, label, onPress, fixColumnStyle, disableDesktopExtraStyle }) => {
  return (
    <Button
      py={{ md: fixColumnStyle ? undefined : 1, base: 4 }}
      px={{ md: disableDesktopExtraStyle ? undefined : 8, base: 4 }}
      pr={{ md: 4 }}
      height={'auto'}
      onClick={onPress}
      m={0}
      variant={'none'}
      borderRadius={0}
      className={
        disableDesktopExtraStyle
          ? classNames({ 'bg-gray100 md:bg-transparent': isActive })
          : classNames('hover:bg-gray100', { 'bg-gray100': isActive })
      }
    >
      <Flex
        w={'full'}
        align={'center'}
        flexDirection={{ md: 'row', base: 'row-reverse' }}
        justifyContent={{ base: 'space-between', md: 'flex-start' }}
      >
        <Stack
          border={'2px solid black'}
          w={5}
          h={5}
          borderRadius={100}
          alignItems={'center'}
          justifyContent={'center'}
          className={'flex-shrink-0'}
        >
          <ConditionalFragment condition={isActive}>
            <span className={'w-2.5 h-2.5 bg-black rounded-xl'} />
          </ConditionalFragment>
        </Stack>
        <Text
          fontSize={16}
          lineHeight={'24px'}
          letterSpacing={0.5}
          fontWeight={'400'}
          textColor={'black'}
          ml={{ md: 2 }}
        >
          {label}
        </Text>
      </Flex>
    </Button>
  );
};
export default CustomRadio;
